import { EDxfEntityKind, IDxfEntity, IDxfPoint, IDxfVertex } from './dxf-entities';
import { EDxfLayerKind } from './dxf-layers';
import { formatDxfNumber, formatDxfReal } from './dxf-out';

/**
 * Which copy of a shape drawn twice survives: the screen draws a filled outline and a stroked one
 * over it, a dashed line over a solid one. The main contour outranks an invisible line, which
 * outranks an axis and a thin line.
 */
const KIND_RANK: readonly EDxfLayerKind[] = [
    EDxfLayerKind.Contour,
    EDxfLayerKind.Hidden,
    EDxfLayerKind.Axis,
    EDxfLayerKind.Thin,
    EDxfLayerKind.Dim,
    EDxfLayerKind.Hatch,
    EDxfLayerKind.Text,
];

function rank(layer: string): number {
    const index: number = KIND_RANK.findIndex((kind: EDxfLayerKind): boolean => layer.endsWith(`_${kind}`));
    return index < 0 ? KIND_RANK.length : index;
}

function at(point: IDxfPoint): string {
    return `${formatDxfNumber(point[0])},${formatDxfNumber(point[1])}`;
}

/** The same segment whichever end it starts from. */
function segmentKey(from: IDxfPoint, to: IDxfPoint): string {
    const a: string = at(from);
    const b: string = at(to);
    return a.localeCompare(b) <= 0 ? `S ${a} ${b}` : `S ${b} ${a}`;
}

/**
 * What an entity looks like in the file, layer aside: two entities with one key draw the same
 * thing at the same place. A two-point straight polyline draws the same as a line.
 */
export function shapeKey(entity: IDxfEntity): string {
    switch (entity.kind) {
        case EDxfEntityKind.Line:
            return segmentKey(entity.from, entity.to);
        case EDxfEntityKind.Polyline: {
            const [first, second]: readonly IDxfVertex[] = entity.vertices;
            if (entity.vertices.length === 2 && !entity.closed && first.bulge === 0) {
                return segmentKey(first.at, second.at);
            }
            const vertices: string = entity.vertices
                .map((vertex: IDxfVertex): string => `${at(vertex.at)}/${formatDxfReal(vertex.bulge)}`)
                .join(' ');
            return `P ${entity.closed ? 'c' : 'o'} ${vertices}`;
        }
        case EDxfEntityKind.Arc:
            return `A ${at(entity.center)} ${formatDxfNumber(entity.radius)} ${formatDxfReal(entity.startDeg)} ${formatDxfReal(entity.endDeg)}`;
        case EDxfEntityKind.Text:
            return `T ${entity.layer} ${at(entity.at)} ${entity.text} ${formatDxfReal(entity.rotationDeg)}`;
        case EDxfEntityKind.Hatch:
            return `H ${entity.pattern} ${entity.boundary.map(at).join(' ')}`;
    }
}

/**
 * The entities with every shape drawn once: of the copies of one shape the one on the
 * highest-ranked layer stays, at the place of the first copy, so the order of the file holds.
 */
export function dedupeEntities(entities: readonly IDxfEntity[]): IDxfEntity[] {
    const kept: Map<string, { index: number; entity: IDxfEntity }> = new Map<string, { index: number; entity: IDxfEntity }>();
    entities.forEach((entity: IDxfEntity, index: number): void => {
        const key: string = shapeKey(entity);
        const seen: { index: number; entity: IDxfEntity } | undefined = kept.get(key);
        if (!seen) {
            kept.set(key, { index, entity });
            return;
        }
        if (rank(entity.layer) < rank(seen.entity.layer)) {
            kept.set(key, { index: seen.index, entity });
        }
    });
    return [...kept.values()]
        .sort((a: { index: number }, b: { index: number }): number => a.index - b.index)
        .map((entry: { entity: IDxfEntity }): IDxfEntity => entry.entity);
}
